import * as React from 'react';
import { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogHeader } from '../atoms/dialog';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Label } from '../atoms/label';
import { InfoSection } from '../molecules/info-section';
import { useTranslation } from 'react-i18next';
import { Info, Rocket, LoaderCircle, X, LucideProps } from 'lucide-react';
// Analytics: project creation events tracked via Redux middleware

interface ConfirmProjectModalProps {
  isOpen: boolean;
  onClose: (source?: 'close-icon' | 'back-button') => void;
  onDelete?: () => void;
  onCreate: (projectName: string) => void;
  title?: string;
  description?: string;
  iconName?: React.ComponentType<LucideProps>;
  inputLabel?: string;
  targetAction?: 'create' | 'delete';
  info?: {
    title: string;
    desc: string;
  };
  buttonText?: string;
  buttonType?: 'primary' | 'secondary' | 'danger';
  expectedProjectName?: string;
  initialProjectName?: string;
  showProjectHint?: boolean;
  isLoading?: boolean;
  isProjectNameLoading?: boolean;
  classNameDescription?: string;
  hasCreatedProject?: boolean;
  isWeb?: boolean;
}

export const ConfirmProjectModal: React.FC<ConfirmProjectModalProps> = ({
  isOpen,
  onClose,
  onDelete,
  onCreate,
  title = '',
  description = '',
  targetAction = 'create',
  iconName,
  inputLabel,
  info = {
    title: '',
    desc: '',
  },
  initialProjectName = '',
  buttonText = null,
  buttonType = 'primary',
  expectedProjectName = null,
  showProjectHint = false,
  isLoading = false,
  isProjectNameLoading = false,
  classNameDescription = '',
  hasCreatedProject = false,
  isWeb = true,
}) => {
  const { t } = useTranslation();
  const [projectName, setProjectName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (projectName.trim() && isValidProjectName()) {
      onCreate(projectName.trim());
      // Analytics: conversation.project.created tracked via Redux middleware
      if (targetAction === 'delete') {
        onDelete?.();
        setProjectName('');
      }
    }
  };

  const isValidProjectName = () => {
    if (!expectedProjectName) {
      return projectName.trim() !== '';
    }
    return projectName.trim() === expectedProjectName.trim();
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setProjectName('');
      onClose('close-icon');
    }
  };

  React.useEffect(() => {
    if (isOpen && initialProjectName.trim() !== '') {
      setProjectName(initialProjectName);
    }
  }, [isOpen, initialProjectName]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        data-testid="confirm-project-modal"
        className={`${showProjectHint ? '' : ''} flex flex-col max-w-[500px] w-[500px] p-6`}
      >
        <DialogHeader className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showProjectHint ? (
              <Rocket data-testid="rocket-icon" size={16} strokeWidth={2} />
            ) : (
              <>
                {targetAction === 'delete'
                  ? iconName && React.createElement(iconName, { size: 18, strokeWidth: 2.5 } as any)
                  : iconName &&
                    React.createElement(iconName, { size: 18, strokeWidth: 2.5 } as any)}
              </>
            )}
            {targetAction === 'delete' ? (
              <DialogTitle data-testid="delete-project-dialog-title">{title}</DialogTitle>
            ) : (
              <DialogTitle data-testid="create-project-dialog-title">{title}</DialogTitle>
            )}
          </div>
          <button
            data-testid="close-button"
            onClick={() => onClose('close-icon')}
            className="dialog-close-button"
          >
            <X color="#667085" strokeWidth={2.5} />
          </button>
        </DialogHeader>

        {description && !showProjectHint && (
          <p className={`dialog-description ${classNameDescription}`}>{description}</p>
        )}

        {showProjectHint && (
          <div className="flex gap-4">
            <div>
              <img src="https://files.ideoz.ai/rocket.gif" alt="rocket" width="90" height="120" />
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="text-xl font-medium text-gray-900">
                Don't lose your breakthrough and save it as a project
              </h3>
              <p className="text-base font-normal text-gray-900">
                Create a project to save your work and continue later. It's free and easy!
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} id="confirm-project-form" className="space-y-6 mb-0">
          <div className={`${showProjectHint ? 'rounded bg-gray-50 p-4' : ''}`}>
            <Label className="mb-3 block" htmlFor="projectName">
              {inputLabel || 'Project name'}
            </Label>
            <div className="relative">
              <Input
                type="text"
                id="projectName"
                data-testid="project-name-input"
                value={projectName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setProjectName(e.target.value)
                }
                autoFocus={true}
                disabled={isProjectNameLoading}
                className={isProjectNameLoading ? 'pr-10' : ''}
              />
              {isProjectNameLoading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <LoaderCircle width={16} height={16} className="animate-spin text-gray-400" />
                </div>
              )}
            </div>
          </div>
        </form>

        {info.title && (!hasCreatedProject || targetAction === 'delete') && (
          <InfoSection title={info.title} description={info.desc} iconName={Info} variant="info" />
        )}

        <DialogFooter>
          {showProjectHint && (
            <Button
              type="button"
              variant="secondary-gray"
              size={isWeb ? 'sm' : 'xs'}
              onClick={() => {
                onClose('back-button');
              }}
            >
              Back
            </Button>
          )}
          <Button
            type="submit"
            form="confirm-project-form"
            variant={
              buttonType === 'primary'
                ? 'primary'
                : buttonType === 'danger'
                  ? 'destructive'
                  : 'outline'
            }
            disabled={!isValidProjectName() || isLoading}
            size={isWeb ? 'sm' : 'xs'}
            data-testid="confirm-project-button"
            data-track="confirm_create_project"
          >
            {buttonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
